import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogOut, Home, User, ChevronDown, Loader2, Check } from "lucide-react";
import { LogoMark } from "@ava/shared/components/design-system";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { trpc } from "@/lib/trpc";

interface AppHeaderProps {
  /** Texto de subtítulo opcional (ej: "Solicitud: ONB-xxx") */
  subtitle?: string;
  /** Mostrar botón de volver al inicio */
  showHomeButton?: boolean;
  /** Callback personalizado para el botón de inicio */
  onHomeClick?: () => void;
}

export function AppHeader({ subtitle, showHomeButton = true, onHomeClick }: AppHeaderProps) {
  const [, setLocation] = useLocation();
  const { user, loading, logout } = useAuth();

  const { data: myClient } = trpc.auth.myClient.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  });
  const isPJ = myClient?.clientType === "PERSONA_JURIDICA";

  const { data: identityStatus } = trpc.onboarding.getIdentityStatus.useQuery({}, {
    enabled: myClient !== undefined && !isPJ,
    staleTime: 5 * 60 * 1000,
  });
  const isIdentityVerified = !!identityStatus?.isValid;

  const useGradient = myClient !== undefined;

  const handleLogout = async () => {
    await logout();
    // Full reload (no setLocation) para evitar race con ProtectedRoute,
    // que al ver user=null en la ruta actual dispararía un segundo redirect
    // a la pantalla de login. El reload limpia estado y aterriza en el Home público.
    window.location.href = "/";
  };

  const handleHomeClick = () => {
    if (onHomeClick) {
      onHomeClick();
    } else {
      setLocation("/");
    }
  };

  // Obtener iniciales del usuario
  const getInitials = (name?: string, email?: string) => {
    if (name) {
      const parts = name.split(" ");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return "US";
  };

  return (
    <>
      <style>{`
        .ava-app-header-ph {
          background: linear-gradient(135deg, #0B3D91 0%, #1565C0 40%, #1E88E5 70%, #3B8FF5 100%) !important;
          color: #ffffff !important;
          border-bottom: none !important;
        }
      `}</style>
      <header className={`sticky top-0 z-50 shadow-sm transition-all ${useGradient ? "ava-app-header-ph" : "border-b border-slate-200 bg-white/95 backdrop-blur-md"}`}>
        <div className="container px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo y título */}
            <div className="flex items-center gap-3">
              <div 
                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleHomeClick}
              >
                {/* Logo: tono según degradado (azul en todos los anchos) */}
                <div className="flex">
                  <LogoMark size={32} tone={useGradient ? "white" : "ink"} />
                </div>
                <div>
                  <h1 className={`text-xl font-bold leading-tight ${useGradient ? "text-white" : "text-foreground"}`}>
                    AVA <span className={`font-semibold ${useGradient ? "text-white/80" : "text-muted-foreground"}`}>Onboarding</span>
                  </h1>
                  {subtitle && (
                    <p className={`text-xs ${useGradient ? "text-white/70" : "text-muted-foreground"}`}>
                      <span className="md:hidden">
                        {subtitle.startsWith("Solicitud: ") ? subtitle.replace("Solicitud: ", "") : subtitle}
                      </span>
                      <span className="hidden md:inline">
                        {subtitle}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Usuario y acciones */}
            <div className="flex items-center gap-3">
              {showHomeButton && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleHomeClick}
                  className="hidden sm:flex gap-2"
                >
                  <Home className="w-4 h-4" />
                  Inicio
                </Button>
              )}

              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Cargando...</span>
                </div>
              ) : user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 px-2 hover:bg-black/5 dark:hover:bg-white/5">
                      <div className="relative">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className={`${useGradient ? "bg-white/20 text-white" : "bg-primary/10 text-primary"} text-xs font-medium`}>
                            {getInitials(user.name ?? undefined, user.email ?? undefined)}
                          </AvatarFallback>
                        </Avatar>
                        {isIdentityVerified && (
                          <div className="absolute -bottom-1 -right-0.5 w-[16px] h-[16px] bg-[#10B981] rounded-full flex items-center justify-center" >
                            <Check className=" text-white stroke-[3]" size={'12'} style={{width:"12px"}}/>
                          </div>
                        )}
                      </div>
                      <div className="hidden sm:flex flex-col items-start">
                        <span className="text-sm font-medium truncate max-w-[150px]">
                          {user.name || user.email}
                        </span>
                        {user.name && user.email && (
                          <span className={`text-xs truncate max-w-[150px] ${useGradient ? "text-white/70" : "text-muted-foreground"}`}>
                            {user.email}
                          </span>
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 ${useGradient ? "text-white/80" : "text-muted-foreground"}`} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{user.name || "Usuario"}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Cerrar Sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  <User className="w-3 h-3 mr-1" />
                  No conectado
                </Badge>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
